import uploadLineSvg from '@raison/static/icons/upload-line.svg';
import {BigNumberFromString, CryptoCurrencyCode, TransactionId} from '@raison/types/new';
import {usePrefetchQuery, useSuspenseQuery} from '@tanstack/react-query';
import BigNumber from 'bignumber.js';
import * as Schema from 'effect/Schema';
import React from 'react';
import {AlertHandler, AlertPane, IAlert} from '~/entities/alerts';
import {
    CryptoCurrencyInfo,
    findCryptoCurrencyByCode,
    useCurrenciesInfoOptions,
    useCurrencyFormat,
} from '~/entities/currencies';
import {useTrTxDetailsOptions} from '../api/fetchTrTxDetails';
import {TRAVEL_RULE_ENABLED} from '../constants';
import {useDerived} from '../lib/useDerivedAtom';
import {createTrTxReceiveRoute, createTrTxSendRoute} from '../model/SendTxRouting';
import {ReceiveTxPreview, TrTxId, TrTxIdSchema, TrTxStatus} from '../model/TrTx';
import {canceledTrTxIds$} from '../model/TrTxIdsRegistry';

const ReceiveSubject = 'receive-order-without-travel-rule-transaction';

const ReceiveTrTxAlert = Schema.Struct({
    subject: Schema.Literal(ReceiveSubject),
    content: Schema.Struct({
        id: TransactionId,
        amount: BigNumberFromString,
        currency: CryptoCurrencyCode,
    }),
});

const receiveHandler: AlertHandler<ReceiveTxPreview> = {
    subject: ReceiveSubject,
    Manager({content, children}) {
        const {id, amount, currency: currencyCode} = Schema.decodeUnknownSync(ReceiveTrTxAlert.fields.content)(content);
        const currencyInfo = useCurrencyInfoByCode(currencyCode);
        return children({id, amount, currency: currencyInfo});
    },
    Display({content: {currency, amount, id}}) {
        const {format: formatAsSubject} = useCurrencyFormat(currency);

        return (
            <AlertPane
                icon={uploadLineSvg}
                messageSummary={`Receiving ${formatAsSubject(amount)}`}
                linkTo={createTrTxReceiveRoute(currency.type, id)}
                color='yellow'
            >
                {"Fill in the sender's details to receive"}
            </AlertPane>
        );
    },
};

export function findReceiveTrTxAlert(orderId: TransactionId) {
    return (alerts: IAlert[]): typeof ReceiveTrTxAlert.fields.content.Type | undefined => {
        for (const alert of alerts) {
            if (alert.subject !== ReceiveSubject) {
                continue;
            }
            const {content} = Schema.decodeUnknownSync(ReceiveTrTxAlert)(alert);
            if (content.id === orderId) {
                return content;
            }
        }
        return undefined;
    };
}

const SentSubject = 'travel-rule-transaction-in-progress';

const SentTrTxAlert = Schema.Struct({
    subject: Schema.Literal(SentSubject),
    content: Schema.Struct({
        id: TrTxIdSchema,
        amount: BigNumberFromString,
        currency: CryptoCurrencyCode,
        status: TrTxStatus,
    }),
});

const decodeSentTrTxContent = Schema.decodeUnknownSync(SentTrTxAlert.fields.content);

const sendHandler: AlertHandler<{
    currency: CryptoCurrencyInfo;
    amount: BigNumber;
    id: TrTxId;
    status: Exclude<TrTxStatus, 'completed'>;
}> = {
    subject: SentSubject,
    Manager({content, children}) {
        const {id, currency, amount, status} = decodeSentTrTxContent(content);
        const currencyInfo = useCurrencyInfoByCode(currency);

        usePrefetchQuery(useTrTxDetailsOptions(id));

        if (status === 'completed') {
            return null;
        }

        return children({id, amount, status, currency: currencyInfo});
    },
    Display({content: {currency, amount, status, id}}) {
        const canceling = useDerived(canceledTrTxIds$, ids => ids.has(id));
        const {format: formatAsSubject} = useCurrencyFormat(currency);

        const t = {
            Heading: `Sending ${formatAsSubject(amount)}`,
            Cancelling: 'The transaction will be cancelled soon',
            statuses: {
                pending: 'The transfer data has been sent for review',
                approved: 'Choose the fee and confirm the transfer',
                rejected: 'The transfer data is rejected',
            },
        };

        return (
            <AlertPane
                icon={uploadLineSvg}
                messageSummary={t.Heading}
                color={
                    (status === 'approved' && 'yellow') ||
                    (status === 'rejected' && 'red') ||
                    (status === 'pending' && 'neutral') ||
                    undefined
                }
                linkTo={createTrTxSendRoute(currency.type, id, status)}
            >
                {canceling ? t.Cancelling : t.statuses[status]}
            </AlertPane>
        );
    },
};

export function isSentTrTxAlert(trtId?: TrTxId) {
    return (alert: IAlert) => {
        return (
            alert.subject === SentSubject && (trtId === undefined || trtId === decodeSentTrTxContent(alert.content).id)
        );
    };
}

export const TrtAlertHandlersBatch = {
    ...(TRAVEL_RULE_ENABLED
        ? {
              [sendHandler.subject]: sendHandler,
              [receiveHandler.subject]: receiveHandler,
          }
        : {}),
};

function useCurrencyInfoByCode(currencyCode: CryptoCurrencyCode) {
    const {data} = useSuspenseQuery({
        ...useCurrenciesInfoOptions(),
        select: findCryptoCurrencyByCode(currencyCode),
    });
    return data;
}
